// role-based-dashboards — Phase 6.2: ungated recent tickets feed widget.
//
// Compact last-5 tickets, deep-linking to the full TicketBrowsePage (spec
// "recent tickets feed (deep-link /dashboard/tickets)") rather than
// re-implementing filtering/pagination/detail-drilldown here.

import { browseTickets } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type { TicketBrowseItem } from '@jq4race/shared';
import { Badge, type BadgeProps, Button, Card, CardContent, Skeleton } from '@jq4race/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { TriangleAlert } from 'lucide-react';
import { translateApiError } from '../../../../shared/errors/translateApiError';

const FEED_LIMIT = 5;

const STATUS_TONE: Record<TicketBrowseItem['status'], BadgeProps['variant']> = {
  parked: 'info',
  requested: 'warning',
  in_transit: 'accent',
  delivered: 'success',
  cancelled: 'danger',
};

export function RecentTicketsFeed() {
  const { t } = useTranslation('dashboard');
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['dashboard-overview', 'recent-tickets'],
    queryFn: () => browseTickets({ limit: FEED_LIMIT }),
  });

  const STATUS_LABEL: Record<TicketBrowseItem['status'], string> = {
    parked: t('ticketStatus.parked'),
    requested: t('ticketStatus.requested'),
    in_transit: t('ticketStatus.inTransit'),
    delivered: t('ticketStatus.delivered'),
    cancelled: t('ticketStatus.cancelled'),
  };

  return (
    <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
      <CardContent className="space-y-3 p-5">
        <div className="flex items-center justify-between">
          <p className="jq-eyebrow">{t('recentTickets.eyebrow')}</p>
          <Button asChild variant="ghost" size="sm" className="text-[13px]">
            <Link to="/dashboard/tickets">{t('recentTickets.viewAll')}</Link>
          </Button>
        </div>

        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: loading skeleton rows
              <Skeleton key={`ticket-skel-${i}`} className="h-5 w-full" />
            ))}
          </div>
        ) : isError ? (
          <p className="flex items-center gap-2 text-[13px] text-jq-danger">
            <TriangleAlert className="h-4 w-4 shrink-0" strokeWidth={1.75} />
            {translateApiError(error, t, { fallback: t('recentTickets.error') })}
          </p>
        ) : data && data.items.length > 0 ? (
          <ul className="divide-y divide-jq-sand/40">
            {data.items.map((ticket) => (
              <li
                key={ticket.id}
                className="flex items-center justify-between gap-3 py-2 text-[13px]"
              >
                <span className="truncate text-jq-charcoal">
                  {t('recentTickets.label', { number: ticket.ticketNumber, plate: ticket.plate })}
                </span>
                <Badge variant={STATUS_TONE[ticket.status]}>{STATUS_LABEL[ticket.status]}</Badge>
              </li>
            ))}
          </ul>
        ) : (
          <p className="py-4 text-center text-[13px] text-jq-stone">{t('recentTickets.empty')}</p>
        )}
      </CardContent>
    </Card>
  );
}
